import * as Dialog from '@radix-ui/react-dialog';
import { type Location } from '@koala/sdk';
import { useEffect, useState } from 'react';
import { POS_UNAVAILABLE_COPY } from './constants';
import { useStoreAvailabilityState } from './hooks';
import * as Styled from './styles';
import { Modal } from '@/components/uielements/modal';
import { KOALA_SESSION_STORAGE_KEYS } from '@/constants/global';
import { STORE_AVAILABILITY_STATE } from '@/utils/locations';

interface Props {
  location?: Location;
}

/**
 * "Can't accept orders right now" modal shown once, on the diner's first visit to a
 * POS-unavailable store within a browser session (TEAM-14640). Built on the Radix `Modal`
 * so dialog semantics — `role="dialog"`, focus trap, focus-move-on-open and focus-return
 * on dismiss — come for free. After dismissal the persistent banner remains.
 *
 * Uses the precedence-aware availability state so it never pops over a higher-priority
 * state (closed-by-hours / operator-paused).
 */
export const PosUnavailableModal = ({ location }: Props) => {
  const availabilityState = useStoreAvailabilityState(location);
  const isPosUnavailable =
    availabilityState === STORE_AVAILABILITY_STATE.POS_UNAVAILABLE ||
    availabilityState === STORE_AVAILABILITY_STATE.OPERATOR_PAUSED;
  const storeId = location?.id;
  const [requestVisibility, setRequestVisibility] = useState(false);

  useEffect(() => {
    if (!isPosUnavailable || storeId == null || typeof window === 'undefined') {
      return;
    }

    const seenKey = `${KOALA_SESSION_STORAGE_KEYS.POS_UNAVAILABLE_MODAL_SEEN}:${storeId}`;
    if (window.sessionStorage.getItem(seenKey)) {
      return;
    }

    window.sessionStorage.setItem(seenKey, '1');
    setRequestVisibility(true);
  }, [isPosUnavailable, storeId]);

  if (!isPosUnavailable) {
    return null;
  }

  const restaurantName = location?.label ?? 'This restaurant';

  return (
    <Modal.Container
      requestVisibility={requestVisibility}
      onCancel={() => setRequestVisibility(false)}
    >
      {(closeModal: () => void) => (
        <Styled.ModalWrapper>
          <Modal.Content>
            <Styled.IconCircle>
              <Styled.ModalIcon title="" aria-hidden focusable={false} />
            </Styled.IconCircle>
            <Dialog.Title asChild>
              <Styled.ModalTitleText>{POS_UNAVAILABLE_COPY.modalTitle}</Styled.ModalTitleText>
            </Dialog.Title>
            <Dialog.Description asChild>
              <Styled.ModalBodyText>
                {POS_UNAVAILABLE_COPY.modalBody(restaurantName)}
              </Styled.ModalBodyText>
            </Dialog.Description>
          </Modal.Content>
          <Modal.Footer>
            <Styled.ModalCloseButton
              type="button"
              $buttonTheme="secondary"
              onClick={() => {
                setRequestVisibility(false);
                closeModal();
              }}
            >
              {POS_UNAVAILABLE_COPY.modalClose}
            </Styled.ModalCloseButton>
          </Modal.Footer>
        </Styled.ModalWrapper>
      )}
    </Modal.Container>
  );
};
